import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import { Button } from "../components/ui/button";
import {
  RefreshCw, MessageCircle, ExternalLink, Sparkles, LogOut, CheckCircle2,
  Rocket, CreditCard,
} from "lucide-react";
import { toast } from "sonner";
import { getLeadByToken, session, getConfig, formatErr } from "../lib/api";

const STATUS = {
  pendiente: { label: "Pendiente", pct: 33, color: "#F59E0B" },
  en_proceso: { label: "En proceso", pct: 66, color: "#3B82F6" },
  entregado: { label: "Entregado", pct: 100, color: "#10B981" },
};
export default function Panel() {
  const navigate = useNavigate();
  const [lead, setLead] = useState(session.load());
  const [config, setConfig] = useState({ whatsapp: "", phone: "", email: "" });
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (!lead) { navigate("/acceso"); return; }
    refresh();
    getConfig().then(setConfig).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = async () => {
    if (!lead?.session_token) return;
    setRefreshing(true);
    try {
      const fresh = await getLeadByToken(lead.session_token);
      session.save(fresh);
      setLead(fresh);
    } catch (err) { toast.error(formatErr(err)); }
    finally { setRefreshing(false); }
  };

  const logout = () => { session.clear(); toast.success("Sesión cerrada"); navigate("/"); };

  if (!lead) return null;
  const stat = STATUS[lead.status] || STATUS.pendiente;
  const wa = (config.whatsapp || "").replace(/\D/g, "");
  const waUrl = wa
    ? `https://wa.me/${wa}?text=${encodeURIComponent(
        `Hola, soy ${lead.contact_name}. Mi código de proyecto es ${lead.access_code}.`
      )}` : "#";

  return (
    <AppShell title="Panel de tu negocio"
      right={
        <button onClick={logout}
          className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center"
          data-testid="btn-logout-client" aria-label="Cerrar sesión">
          <LogOut className="w-4 h-4 text-slate-700" />
        </button>
      }>
      <div className="px-5 pb-6 stagger">
        <div className="rounded-2xl border border-slate-200 p-4 bg-white" data-testid="welcome-card">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[var(--el-red)]" />
            <span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
              Hola, {lead.contact_name.split(" ")[0]}
            </span>
          </div>
          <p className="text-sm text-slate-700 mt-1">
            {lead.prototype_url ? "¡Tu prototipo ya está disponible!" : "Tu proyecto está en proceso. Te avisamos en cuanto esté tu prototipo."}
          </p>
        </div>

        {/* Prototype banner */}
        {lead.prototype_url && (
          <div className="mt-4 rounded-2xl bg-gradient-to-br from-[var(--el-red)] to-[#7A1A1E] text-white p-4 shadow-[0_10px_30px_rgba(155,34,38,0.25)]" data-testid="prototype-card">
            <div className="flex items-center gap-2">
              <Rocket className="w-4 h-4" />
              <span className="text-[11px] font-bold uppercase tracking-[0.15em]">Prototipo listo</span>
            </div>
            <h3 className="mt-1 font-[Outfit] font-bold text-lg">Tu prototipo ya está disponible</h3>
            <p className="text-sm text-white/85 mt-0.5">Revísalo y dinos qué te parece.</p>
            <a href={lead.prototype_url} target="_blank" rel="noreferrer"
              className="mt-3 inline-flex items-center gap-2 bg-white text-[var(--el-red)] h-10 px-4 rounded-xl font-semibold text-sm active:scale-[0.98] transition"
              data-testid="btn-open-prototype">
              <ExternalLink className="w-4 h-4" /> Abrir prototipo
            </a>
          </div>
        )}

        {/* Status */}
        <div className="mt-4 rounded-2xl border border-slate-200 p-4" data-testid="project-status">
          <div className="flex items-center justify-between">
            <h3 className="font-[Outfit] font-semibold text-slate-900">Estado del proyecto</h3>
            <button onClick={refresh}
              className="h-8 w-8 rounded-full hover:bg-slate-100 flex items-center justify-center"
              data-testid="btn-refresh-status" aria-label="Actualizar">
              <RefreshCw className={`w-4 h-4 text-slate-700 ${refreshing ? "animate-spin" : ""}`} />
            </button>
          </div>
          <div className="mt-3">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-sm font-medium" style={{ color: stat.color }}>{stat.label}</span>
              <span className="text-xs text-slate-500">{stat.pct}%</span>
            </div>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full rounded-full transition-all"
                style={{ width: `${stat.pct}%`, backgroundColor: stat.color }} />
            </div>
          </div>
          <ol className="mt-4 space-y-2">
            {[
              { key: "pendiente", label: "Pendiente" },
              { key: "en_proceso", label: "En proceso" },
              { key: "entregado", label: "Entregado" },
            ].map((s, i) => {
              const order = ["pendiente", "en_proceso", "entregado"];
              const reached = order.indexOf(lead.status) >= i;
              return (
                <li key={s.key}
                  className={`flex items-center gap-2.5 text-sm ${reached ? "text-slate-900" : "text-slate-400"}`}
                  data-testid={`step-${s.key}`}>
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center ${reached ? "bg-emerald-500" : "bg-slate-200"}`}>
                    {reached ? <CheckCircle2 className="w-3 h-3 text-white" />
                             : <span className="w-1.5 h-1.5 bg-slate-400 rounded-full" />}
                  </span>
                  {s.label}
                </li>
              );
            })}
          </ol>
        </div>

        {/* Payment indicator */}
        {lead.selected_plan && lead.selected_plan !== "gratuito" && (
          <div className="mt-4 rounded-2xl border border-slate-200 p-4">
            <div className="flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-slate-700" />
              <h3 className="font-[Outfit] font-semibold text-slate-900">Pagos</h3>
            </div>
            <div className="mt-3 space-y-2">
              <PayRow label="1er pago (50% inicial)" done={lead.paid_first} />
              <PayRow label="2do pago (al entregar)" done={lead.paid_second} />
            </div>
          </div>
        )}

        {/* Project info */}
        <div className="mt-4 rounded-2xl border border-slate-200 p-4">
          <h3 className="font-[Outfit] font-semibold text-slate-900">Tu proyecto</h3>
          <dl className="mt-2 space-y-1.5 text-sm">
            <Row k="Negocio" v={lead.business_name || lead.business_type} />
            <Row k="Tipo" v={lead.business_type} />
            <Row k="Estilo" v={lead.style || "—"} />
            <Row k="Email" v={lead.email} />
            <Row k="Plan" v={(lead.selected_plan || "—").toUpperCase()} />
            <Row k="Código" v={lead.access_code} mono />
          </dl>
        </div>

        {/* Actions */}
        <div className="mt-4 grid grid-cols-1 gap-2.5">
          <a href={waUrl} target="_blank" rel="noreferrer"
            className="h-11 rounded-xl bg-[#25D366] text-white font-semibold flex items-center justify-center gap-2 active:scale-[0.99] transition"
            data-testid="btn-request-changes">
            <MessageCircle className="w-4 h-4" />Solicitar cambios
          </a>
          <Button onClick={() => navigate("/soporte")}
            className="h-11 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-semibold"
            data-testid="btn-contact-support">
            Contactar soporte
          </Button>
        </div>
      </div>
    </AppShell>
  );
}

const Row = ({ k, v, mono }) => (
  <div className="flex items-start justify-between gap-4">
    <dt className="text-slate-500 text-xs uppercase tracking-wider mt-1">{k}</dt>
    <dd className={`text-sm text-slate-900 text-right ${mono ? "font-mono tracking-widest" : ""}`}>{v || "—"}</dd>
  </div>
);

const PayRow = ({ label, done }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-slate-700">{label}</span>
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-full ${
      done ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
           : "bg-slate-50 text-slate-500 border border-slate-200"
    }`}>
      <span className={`w-1.5 h-1.5 rounded-full ${done ? "bg-emerald-500" : "bg-slate-400"}`} />
      {done ? "Pagado" : "Pendiente"}
    </span>
  </div>
);
